import { useMemo, useRef, useState } from "react";
import type { CityMapStateDto } from "../../shared/api";
import { provinceMapFeatures } from "../data/turkey-map";

interface TurkeyCityMapProps {
  cities: CityMapStateDto[];
  selectedCode: string | null;
  onSelect: (code: string) => void;
}

function statusLabel(status: CityMapStateDto["status"]) {
  return status === "paid" ? "Bugün kullanıcı mesajı var" : "Bugün mesaj yok";
}

export function TurkeyCityMap({ cities, selectedCode, onSelect }: TurkeyCityMapProps) {
  const cityByCode = useMemo(() => new Map(cities.map((city) => [city.code, city])), [cities]);
  const pathRefs = useRef(new Map<string, SVGPathElement>());
  const [hoveredCode, setHoveredCode] = useState<string | null>(null);
  const [focusedCode, setFocusedCode] = useState<string | null>(null);
  const activeInfoCode = hoveredCode ?? focusedCode ?? selectedCode;
  const activeInfoFeature = provinceMapFeatures.find((feature) => feature.code === activeInfoCode);
  const activeInfoStatus = activeInfoCode
    ? (cityByCode.get(activeInfoCode)?.status ?? "empty")
    : null;

  function moveFocus(code: string, key: string) {
    const currentIndex = provinceMapFeatures.findIndex((feature) => feature.code === code);
    let nextIndex = currentIndex;
    if (key === "ArrowRight" || key === "ArrowDown") nextIndex = currentIndex + 1;
    if (key === "ArrowLeft" || key === "ArrowUp") nextIndex = currentIndex - 1;
    if (key === "Home") nextIndex = 0;
    if (key === "End") nextIndex = provinceMapFeatures.length - 1;
    nextIndex = (nextIndex + provinceMapFeatures.length) % provinceMapFeatures.length;
    pathRefs.current.get(provinceMapFeatures[nextIndex].code)?.focus();
  }

  return (
    <div className="map-frame">
      <svg
        className="turkey-map"
        viewBox="0 80 1050 585"
        role="group"
        aria-label="Türkiye'nin 81 ilini gösteren etkileşimli harita"
      >
        <g>
          {provinceMapFeatures.map((feature) => {
            const city = cityByCode.get(feature.code);
            const status = city?.status ?? "empty";
            const readableStatus = statusLabel(status);
            const selected = selectedCode === feature.code;
            return (
              <g key={feature.code}>
                <title>{`${feature.name}: ${readableStatus}`}</title>
                <path
                  ref={(element) => {
                    if (element) pathRefs.current.set(feature.code, element);
                  }}
                  d={feature.path}
                  className={`province province--${status}${selected ? " province--selected" : ""}`}
                  role="button"
                  aria-label={`${feature.ariaLabel}: ${readableStatus}`}
                  aria-pressed={selected}
                  tabIndex={selected || (!selectedCode && feature.plateNumber === 1) ? 0 : -1}
                  onClick={() => onSelect(feature.code)}
                  onMouseEnter={() => setHoveredCode(feature.code)}
                  onMouseLeave={() =>
                    setHoveredCode((current) => (current === feature.code ? null : current))
                  }
                  onFocus={() => setFocusedCode(feature.code)}
                  onBlur={() =>
                    setFocusedCode((current) => (current === feature.code ? null : current))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelect(feature.code);
                    }
                    if (
                      ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp", "Home", "End"].includes(
                        event.key
                      )
                    ) {
                      event.preventDefault();
                      moveFocus(feature.code, event.key);
                    }
                  }}
                />
              </g>
            );
          })}
        </g>
      </svg>
      {activeInfoFeature && activeInfoStatus && (
        <div className="map-info" data-status={activeInfoStatus}>
          <strong>{activeInfoFeature.name}</strong>
          <span>{statusLabel(activeInfoStatus)}</span>
        </div>
      )}
      <div className="map-legend" aria-label="Harita açıklaması">
        <span>
          <i className="legend-dot legend-dot--paid" /> Bugün kullanıcı mesajı var
        </span>
        <span>
          <i className="legend-dot legend-dot--empty" /> Bugün mesaj yok
        </span>
      </div>
    </div>
  );
}
